// SPDX-FileCopyrightText: 2026 Ben Richardson
// SPDX-License-Identifier: AGPL-3.0-or-later
//
// VIEW 4 — Propagation.
//
// The question: did this arrive somewhere and travel, or appear everywhere at once?
//
// Rows sort by time-of-first-exceedance, which renders a travelling plume as a staircase and a
// basin filling under an inversion as a vertical wall. That sort is also the chart's own biggest
// risk — sorting by arrival time will always LOOK like a staircase — so the geographic re-sort
// toggle is not optional: without it the chart proves itself.

import type { Bundle } from '../data';
import { loadEpisodeSlice } from '../data';
import type { EpisodeSlice } from '../types';
import { el, emptyState, legend, panel, select, xScroll } from '../components/ui';
import { concentration, prettyHour, tip, UNIT } from '../format';
import { ABOVE_SCALE, bandColour, bandLabel, BANDS, HOUR_BOUNDS } from '../palette';
import { getState, setState } from '../state';
import { openStation } from './stationDetail';
import { glossaryLink } from '../glossary';

const CELL_W = 9;
const CELL_H = 15;
const LABEL_W = 128;

function localStamp(utcHour: number): string {
  const date = new Date((utcHour + 10) * 3_600_000);
  return `${date.toISOString().slice(0, 10)}T${String(date.getUTCHours()).padStart(2, '0')}:00`;
}

/** Above this the matrix stops being legible and starts being a performance problem. The longest
 *  episode in the archive runs 1,548 hours across 61 clusters — 94,000 cells, which no reader can
 *  parse and no browser should be asked to draw. Both caps are announced, never silent. */
const MAX_ROWS = 24;
const WINDOW_HOURS = 84;

function drawMatrix(
  container: HTMLElement,
  bundle: Bundle,
  slice: EpisodeSlice,
  order: 'arrival' | 'north',
  peakHour: number,
  full: boolean
): { hiddenRows: number; hiddenHours: number } {
  const allRows = [...slice.rows].sort((a, b) => {
    if (order === 'north') {
      const stationA = bundle.stations.find((s) => s.id === a.clusterId);
      const stationB = bundle.stations.find((s) => s.id === b.clusterId);
      return (stationB?.lat ?? 0) - (stationA?.lat ?? 0);
    }
    return a.first - b.first || b.peak - a.peak;
  });

  // Keep the biggest clusters, then restore the chosen ordering — dropping the tail by peak
  // rather than by position means the rows that survive are the ones that carry the episode.
  const keep = full
    ? allRows
    : [...allRows].sort((a, b) => b.peak - a.peak).slice(0, MAX_ROWS);
  const rows = allRows.filter((row) => keep.includes(row));
  const hiddenRows = allRows.length - rows.length;

  const totalHours = slice.to - slice.from + 1;
  const centre = Math.max(0, Math.min(totalHours - 1, peakHour - slice.from));
  const windowFrom = full ? 0 : Math.max(0, centre - WINDOW_HOURS);
  const windowTo = full ? totalHours - 1 : Math.min(totalHours - 1, centre + WINDOW_HOURS);
  const hiddenHours = totalHours - (windowTo - windowFrom + 1);
  const hours = windowTo - windowFrom + 1;
  const width = LABEL_W + hours * CELL_W + 60;
  const height = rows.length * CELL_H + 44;

  const wrap = el('div', 'matrix-wrap');
  const node = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  node.setAttribute('viewBox', `0 0 ${width} ${height}`);
  node.setAttribute('class', 'matrix');
  node.setAttribute('role', 'img');
  node.setAttribute('aria-label', 'Station by hour concentration matrix');
  node.style.width = `${width}px`;
  node.style.height = `${height}px`;

  const make = (tag: string, attrs: Record<string, string | number>) => {
    const element = document.createElementNS('http://www.w3.org/2000/svg', tag);
    for (const [key, value] of Object.entries(attrs)) element.setAttribute(key, String(value));
    return element;
  };

  // Hour axis: label every sixth hour so the ticks stay readable at 9px per cell.
  const step = hours > 400 ? 24 : hours > 160 ? 12 : 6;
  for (let h = 0; h < hours; h += step) {
    const stamp = localStamp(slice.from + windowFrom + h);
    const text = make('text', {
      x: LABEL_W + h * CELL_W,
      y: 12,
      fill: 'var(--text-tertiary)',
      'font-size': 9,
    });
    text.textContent = `${stamp.slice(11, 13)}:00`;
    node.append(text);
    const day = make('text', { x: LABEL_W + h * CELL_W, y: 22, fill: 'var(--text-muted)', 'font-size': 8 });
    day.textContent = stamp.slice(5, 10);
    node.append(day);
  }

  rows.forEach((row, index) => {
    const y = 30 + index * CELL_H;
    const label = make('text', {
      x: 0,
      y: y + CELL_H - 4,
      fill: 'var(--text-secondary)',
      'font-size': 10,
    });
    label.textContent = row.name.length > 20 ? `${row.name.slice(0, 19)}…` : row.name;
    label.setAttribute('data-tip', tip(`${row.name} (${row.jurisdiction})`, `${row.km} km from the first cluster to exceed`, 'Click to open this station'));
    label.style.cursor = 'pointer';
    label.addEventListener('click', () => {
      const station = bundle.stations.find((s) => s.id === row.clusterId);
      if (station) void openStation(station);
    });
    node.append(label);

    for (let h = 0; h < hours; h++) {
      const cell = row.values[windowFrom + h];
      const value = cell?.[0] ?? null;
      const aboveScale = value !== null && value > HOUR_BOUNDS['pm2.5'][HOUR_BOUNDS['pm2.5'].length - 1];
      const rect = make('rect', {
        x: LABEL_W + h * CELL_W,
        y,
        width: CELL_W - 1,
        height: CELL_H - 1,
        rx: 1,
        fill: value === null ? 'var(--missing)' : aboveScale ? ABOVE_SCALE : bandColour(value),
        'fill-opacity': value === null ? 0.35 : 1,
      });
      if (value === null) rect.setAttribute('stroke-dasharray', '2 2');
      rect.setAttribute(
        'data-tip',
        tip(
          `${row.name}`,
          prettyHour(localStamp(slice.from + windowFrom + h)),
          value === null ? 'No reading this hour' : `PM2.5 ${concentration(value)} ${UNIT} — ${bandLabel(value)}`,
          cell?.[1] !== null && cell?.[1] !== undefined ? `PM10 ${concentration(cell[1])} ${UNIT}` : null,
          `${windowFrom + h - (row.first - slice.from)} hours since this cluster first exceeded`
        )
      );
      node.append(rect);
    }

    const distance = make('text', {
      x: LABEL_W + hours * CELL_W + 6,
      y: y + CELL_H - 4,
      fill: 'var(--text-muted)',
      'font-size': 9,
    });
    distance.textContent = `${row.km} km`;
    node.append(distance);
  });

  wrap.append(node);
  container.append(xScroll(wrap));
  return { hiddenRows, hiddenHours };
}

export async function renderPropagation({
  bundle,
  container,
}: {
  bundle: Bundle;
  container: HTMLElement;
}): Promise<void> {
  const state = getState();
  const episode = bundle.episodes.find((candidate) => candidate.id === state.episode) ?? bundle.episodes[0];

  const view = panel({
    title: 'Propagation',
    subtitle:
      'One row per cluster, one column per hour. Rows are ordered by when each cluster first exceeded, so an arriving plume draws a staircase and a valley filling under an inversion draws a wall.',
  });

  const controls = el('div', 'control-row');
  const options = bundle.episodes
    .slice()
    .sort((a, b) => a.rankReach - b.rankReach)
    .slice(0, 200)
    .map((candidate) => ({
      value: candidate.id,
      label: `${prettyHour(candidate.peakLocal)} — ${candidate.peakClusterName}`,
    }));
  controls.append(select('Episode', options, episode?.id ?? '', (value) => setState({ episode: value })));

  type Order = 'arrival' | 'north';
  let order: Order = 'arrival';
  const orderRow = el('div', 'segmented');
  const orders: Array<[Order, string, string]> = [
    ['arrival', 'By arrival', 'Rows ordered by when each cluster first exceeded'],
    ['north', 'North to south', 'Rows ordered geographically — the honest check on the staircase'],
  ];
  for (const [value, label, tipText] of orders) {
    const button = el('button', `segment${value === order ? ' segment-on' : ''}`);
    button.type = 'button';
    button.textContent = label;
    button.setAttribute('data-tip', tipText);
    button.addEventListener('click', () => {
      order = value;
      [...orderRow.children].forEach((child, index) => child.classList.toggle('segment-on', orders[index][0] === order));
      redraw();
    });
    orderRow.append(button);
  }
  controls.append(orderRow);
  view.body.append(controls);

  const host = el('div');
  view.body.append(host);
  container.append(view.root);

  if (!episode) {
    host.append(emptyState('No episode selected.'));
    return;
  }

  const slice = await loadEpisodeSlice(episode.id);
  if (!slice) {
    host.append(
      emptyState(
        'No hourly detail is published for this episode.',
        'Hourly slices exist for the 200 episodes with the widest reach.'
      )
    );
    return;
  }

  let full = false;
  function redraw(): void {
    host.innerHTML = '';
    const { hiddenRows, hiddenHours } = drawMatrix(host, bundle, slice!, order, episode.peakHour, full);
    // A view that quietly caps what it draws reads as "this is everything". Say what is hidden.
    if (hiddenRows || hiddenHours) {
      const notice = el('p', 'note');
      notice.textContent =
        `Showing the ${MAX_ROWS} clusters with the highest peak and the ${WINDOW_HOURS * 2 + 1} hours around the peak — ` +
        `${hiddenRows} cluster${hiddenRows === 1 ? '' : 's'} and ${hiddenHours} hour${hiddenHours === 1 ? '' : 's'} are not drawn. ` +
        `This episode ran ${episode.durationHours} hours across ${episode.clusterCount} clusters.`;
      const button = el('button', 'button');
      button.type = 'button';
      button.textContent = 'Draw all of it';
      button.addEventListener('click', () => {
        full = true;
        redraw();
      });
      notice.append(document.createElement('br'), button);
      host.append(notice);
    } else if (full) {
      const notice = el('p', 'note');
      notice.textContent = 'Drawing every cluster and every hour of this episode.';
      const button = el('button', 'button');
      button.type = 'button';
      button.textContent = 'Back to the peak window';
      button.addEventListener('click', () => {
        full = false;
        redraw();
      });
      notice.append(document.createElement('br'), button);
      host.append(notice);
    }
  }
  redraw();

  view.body.append(
    legend([
      ...BANDS.map((band) => ({ label: band.label, hex: band.hex })),
      { label: 'above the scale', hex: ABOVE_SCALE },
      { label: 'no reading', hex: 'var(--missing)' },
    ])
  );

  const speedNote =
    episode.frontKmH !== null && episode.frontR2 !== null
      ? `Regressing each cluster's distance from the first exceedance against the hours it took to reach them gives <strong>${episode.frontKmH} km/h</strong>, R² <strong>${episode.frontR2}</strong>. ` +
        (episode.frontR2 > 0.5
          ? 'An R² that high means the arrivals really do line up with distance — this front travelled.'
          : 'An R² that low means the arrivals do not line up with distance at all. Whatever this was, it did not sweep across the network from one direction; switch the row order to north-to-south and the staircase disappears, which is the point of that toggle.')
      : 'Too few clusters saw this episode to fit a front speed.';

  view.body.append(
    el(
      'p',
      'note',
      `${speedNote} The ordering by arrival time will always produce a staircase — that is what sorting by arrival does — so the north-to-south toggle is the check: if the diagonal survives a geographic sort, it is in the data rather than in the sort. ` +
        `Hours with no reading are drawn as dashed grey cells rather than left blank, because a gap in an instrument's record is not clean air. Readings above the top of the published scale get their own pale treatment; ${glossaryLink(
          'suspect',
          'single-cluster episodes'
        )} are still shown.`
    )
  );
}
